// frontend/src/components/SpecificationGenerator.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiClient from '../utils/apiClient';
import Modal from './Modal';
import Card from './ui/card';
import Button from './ui/button';
import Input from './ui/input';
import Select from './ui/select';
import Spinner from './ui/spinner';
import CopyButton from './ui/CopyButton';
import ModernSpecificationViewer from './ModernSpecificationViewer';
import './ui/SpecificationGenerator.css';

const ProjectSection = ({ project }) => {
  if (!project || Object.keys(project).length === 0) return null;

  return (
    <section>
      <h2>{project.name}</h2>
      <p><strong>Goal:</strong> {project.goal}</p>
      {project.constraints?.length > 0 && (
        <ul>
          {project.constraints.map((constraint, index) => (
            <li key={index}>{constraint}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

const FeatureSection = ({ feature }) => {
  if (!feature || Object.keys(feature).length === 0) return null;

  return (
    <section>
      <h2>{feature.name}</h2>
      <p>{feature.description}</p>

      {feature.functional_requirements?.length > 0 && (
        <div>
          <h3>Functional Requirements</h3>
          {feature.functional_requirements.map(req => (
            <div key={req.id}>
              <h4>{req.id}: {req.description}</h4>
              {req.acceptance_criteria?.length > 0 && (
                <ul>
                  {req.acceptance_criteria.map((criteria, index) => (
                    <li key={index}>{criteria}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {feature.non_functional_requirements?.length > 0 && (
        <div>
          <h3>Non-Functional Requirements</h3>
          {feature.non_functional_requirements.map(req => (
            <div key={req.id}>
              <h4>{req.id}: {req.description}</h4>
              {req.acceptance_criteria?.length > 0 && (
                <ul>
                  {req.acceptance_criteria.map((criteria, index) => (
                    <li key={index}>{criteria}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const StakeholdersSection = ({ stakeholders }) => {
  if (!stakeholders || stakeholders.length === 0) return null;

  return (
    <section>
      <h2>Stakeholders</h2>
      <ul>
        {stakeholders.map((stakeholder, index) => (
          <li key={index}><strong>{stakeholder.role}:</strong> {stakeholder.name}</li>
        ))}
      </ul>
    </section>
  );
};

const MetadataSection = ({ metadata }) => {
  if (!metadata || Object.keys(metadata).length === 0) return null;

  return (
    <section>
      <h2>Metadata</h2>
      <p><strong>Generated At:</strong> {new Date(metadata.generatedAt).toLocaleString()}</p>
      <p><strong>Generated By:</strong> {metadata.generatedBy}</p>
      <p><strong>Input Description:</strong> {metadata.inputDescription}</p>
    </section>
  );
};

const SpecificationGenerator = ({ isOpen, onClose, onSpecificationGenerated, onSpecificationSaved }) => {
  const { t, i18n } = useTranslation();

  // Fonction utilitaire pour gérer les objets de traduction
  const getLocalizedText = (textObj, fallback = '') => {
    if (typeof textObj === 'string') return textObj;
    if (typeof textObj === 'object' && textObj) {
      const currentLanguage = i18n.language || 'fr';
      return textObj[currentLanguage] || textObj['fr'] || textObj['en'] || fallback;
    }
    return fallback;
  };

  // Fonction utilitaire pour vérifier si une description est valide
  const isDescriptionValid = () => {
    const description = getLocalizedText(formData.description, formData.description);
    return description && typeof description === 'string' && description.trim().length > 0;
  };

  // Form states
  const [formData, setFormData] = useState({
    description: '',
    context: '',
    priority: 'medium',
    businessObjectives: '',
    constraints: '',
    stakeholders: [],
    existingFeatures: []
  });

  // Interface states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedSpec, setGeneratedSpec] = useState(null);
  const [activeTab, setActiveTab] = useState('input');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [options, setOptions] = useState({
    format: 'complete',
    includeAcceptanceCriteria: true,
    includeUserStories: true,
    includeTestCases: true,
    includeFunctionalRequirements: true,
    includeRisks: true,
    includeBusinessValue: true,
    language: 'fr',
    // 🚀 Options avancées - TOUJOURS OBLIGATOIRES
    useContextualGeneration: true,   // Toujours activé pour maximiser la valeur
    includeJiraExport: true,         // Toujours activé pour l'intégration
    includeConfluenceExport: true    // Toujours activé pour la documentation
  });

  // State for stakeholders
  const [newStakeholder, setNewStakeholder] = useState({
    name: '',
    role: '',
    responsibilities: ''
  });

  // State for export and display
  const [exportFormat, setExportFormat] = useState('json');
  const [showPreview, setShowPreview] = useState(false);
  // 🚀 Nouveau state pour le contexte architectural
  const [architecturalContext, setArchitecturalContext] = useState(null);
  const [exportResults, setExportResults] = useState(null);

  // Charger les templates au montage
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      const response = await apiClient.get('/api/specifications/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Erreur chargement templates:', error);
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template && template.example) {
      // Utiliser la fonction utilitaire pour extraire les textes localisés
      const localizedExample = {
        description: getLocalizedText(template.example.description, template.example.description),
        context: getLocalizedText(template.example.context, template.example.context),
        businessObjectives: getLocalizedText(template.example.businessObjectives, template.example.businessObjectives),
        constraints: getLocalizedText(template.example.constraints, template.example.constraints),
        stakeholders: template.example.stakeholders || [],
        priority: template.example.priority
      };

      setFormData(prev => ({
        ...prev,
        ...localizedExample
      }));
      setSelectedTemplate(templateId);
    }
  };

  const addStakeholder = () => {
    if (newStakeholder.name && newStakeholder.role) {
      setFormData(prev => ({
        ...prev,
        stakeholders: [...prev.stakeholders, { ...newStakeholder }]
      }));
      setNewStakeholder({ name: '', role: '', responsibilities: '' });
    }
  };

  const removeStakeholder = (index) => {
    setFormData(prev => ({
      ...prev,
      stakeholders: prev.stakeholders.filter((_, i) => i !== index)
    }));
  };

  const generateSpecification = async () => {
    // Vérifier et normaliser la description
    const description = getLocalizedText(formData.description, formData.description);

    if (!description || typeof description !== 'string' || !description.trim()) {
      toast.error(t('specificationGenerator.messages.descriptionRequired'));
      return;
    }

    setLoading(true);
    setError(null);
    setArchitecturalContext(null);
    setExportResults(null);

    try {
      // 🚀 Toujours utiliser l'endpoint contextuel maintenant que c'est obligatoire
      const endpoint = '/api/specifications/generate-contextual';

      console.log('🎯 [GENERATE] Mode: Contextuel Avancé (Obligatoire)');

      // Normaliser toutes les données texte du formulaire
      const normalizedFormData = {
        ...formData,
        description: description, // Utiliser la description normalisée
        context: getLocalizedText(formData.context, formData.context),
        businessObjectives: getLocalizedText(formData.businessObjectives, formData.businessObjectives),
        constraints: getLocalizedText(formData.constraints, formData.constraints)
      };

      // 🚀 S'assurer que les exports sont toujours activés
      const enhancedOptions = {
        ...options,
        includeJiraExport: true,         // Force toujours activé
        includeConfluenceExport: true    // Force toujours activé
      };

      const response = await apiClient.post(endpoint, {
        ...normalizedFormData,
        options: enhancedOptions
      });

      console.log('📋 [GENERATE] Réponse reçue:', {
        hasSpecification: !!response.data.specification,
        hasContext: !!response.data.context,
        hasExports: !!response.data.exports
      });

      // Mode contextuel - plus de données
      setGeneratedSpec(response.data.specification);
      setArchitecturalContext(response.data.context);
      setExportResults(response.data.exports);

      console.log('🧠 [CONTEXTUAL] Contexte architectural reçu:', {
        hasContext: !!response.data.context,
        hasExports: !!response.data.exports,
        nodesAnalyzed: response.data.metadata?.nodesAnalyzed,
        dependencies: response.data.metadata?.dependenciesIdentified,
        conflicts: response.data.metadata?.conflictsDetected,
        contextKeys: response.data.context ? Object.keys(response.data.context) : [],
        exportKeys: response.data.exports ? Object.keys(response.data.exports) : []
      });

      setActiveTab('result');

      if (onSpecificationGenerated) {
        onSpecificationGenerated(response.data.specification);
      }

    } catch (error) {
      console.error('❌ Erreur génération spécification:', error);
      const errorMessage = error.response?.data?.error || error.message || t('specificationGenerator.alerts.unknownError');
      setError(errorMessage);
      toast.error(t('specificationGenerator.alerts.generationError', { error: errorMessage }));
    } finally {
      setLoading(false);
    }
  };

  const saveSpecification = async () => {
    if (!generatedSpec) return;

    setLoading(true);
    try {
      console.log('🔍 [SAVE] Debug generatedSpec:', {
        generatedSpec,
        type: typeof generatedSpec,
        keys: Object.keys(generatedSpec || {}),
        hasProject: !!generatedSpec?.project,
        hasFeature: !!generatedSpec?.feature
      });

      // Adapter la structure pour l'API backend
      // Le backend s'attend à { specification: { specification: {...} } }
      // mais on reçoit du générateur directement la spec, donc il faut l'emballer correctement
      const savePayload = {
        specification: {
          specification: generatedSpec  // Ici on wrappe la spec générée
        },
        saveAsNode: true
      };

      console.log('💾 [SAVE] Sauvegarde payload final:', {
        hasSpecification: !!savePayload.specification,
        hasNestedSpec: !!savePayload.specification?.specification,
        nestedSpecKeys: savePayload.specification?.specification ? Object.keys(savePayload.specification.specification) : [],
        structure: 'specification.specification',
        saveAsNode: savePayload.saveAsNode
      });

      // 1. Sauvegarder via API
      await apiClient.post('/api/specifications/save', savePayload);

      // 2. Sauvegarder aussi dans localStorage via le callback
      if (onSpecificationGenerated) {
        onSpecificationGenerated(generatedSpec);
      }

      // 3. Déclencher le refresh de la liste des spécifications
      if (onSpecificationSaved) {
        onSpecificationSaved();
      }

      toast.success(t('specificationGenerator.alerts.savedSuccessfully'));

    } catch (error) {
      console.error('Erreur sauvegarde API:', error);

      // In case of API error, save locally anyway
      if (onSpecificationGenerated) {
        onSpecificationGenerated(generatedSpec);

        // Déclencher le refresh même en cas d'erreur API
        if (onSpecificationSaved) {
          onSpecificationSaved();
        }

        toast.info(t('specificationGenerator.alerts.savedLocally', { error: error.response?.data?.error || error.message }));
      } else {
        toast.error(t('specificationGenerator.alerts.saveError', { error: error.response?.data?.error || error.message }));
      }
    } finally {
      setLoading(false);
    }
  };

  const exportSpecification = () => {
    if (!generatedSpec) return;

    let content, filename, mimeType;

    switch (exportFormat) {
      case 'json':
        content = JSON.stringify(generatedSpec, null, 2);
        filename = 'specification.json';
        mimeType = 'application/json';
        break;
      case 'markdown':
        content = convertToMarkdown(generatedSpec);
        filename = 'specification.md';
        mimeType = 'text/markdown';
        break;
      case 'txt':
        content = convertToText(generatedSpec);
        filename = 'specification.txt';
        mimeType = 'text/plain';
        break;
      default:
        return;
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 🚀 Nouvelle fonction pour télécharger les exports JIRA/Confluence
  const downloadExport = (type, data) => {
    let content, filename, mimeType;

    switch (type) {
      case 'jira':
        content = JSON.stringify(data, null, 2);
        filename = 'jira-export.json';
        mimeType = 'application/json';
        break;
      case 'confluence':
        content = data.content;
        filename = 'confluence-export.html';
        mimeType = 'text/html';
        break;
      default:
        return;
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const convertToMarkdown = (spec) => {
    const s = spec.specification || spec;
    let md = `# ${s.project?.name || s.title || 'Specification'}\n\n`;

    if (s.project?.description || s.summary) {
      md += `## Résumé\n${s.project?.description || s.summary}\n\n`;
    }

    if (s.functional_specification?.description || s.functionalDescription) {
      md += `## Description Fonctionnelle\n${s.functional_specification?.description || s.functionalDescription}\n\n`;
    }

    if (s.functional_specification?.feature) {
      md += `## Fonctionnalité\n${s.functional_specification.feature}\n\n`;
    }

    if (s.functional_specification?.requirements && s.functional_specification.requirements.length > 0) {
      md += `## Exigences Fonctionnelles\n\n`;
      s.functional_specification.requirements.forEach(req => {
        md += `### ${req.id} - ${req.description}\n\n`;
        if (req.acceptance_criteria && req.acceptance_criteria.length > 0) {
          md += `**Critères d'acceptation:**\n`;
          req.acceptance_criteria.forEach(criteria => {
            md += `- ${criteria}\n`;
          });
          md += '\n';
        }
      });
    }

    if (s.userStories && s.userStories.length > 0) {
      md += `## User Stories\n\n`;
      s.userStories.forEach(story => {
        md += `### ${story.title}\n`;
        md += `**ID:** ${story.id}\n`;
        md += `**Story:** ${story.story}\n`;
        md += `**Priorité:** ${story.priority}\n\n`;
      });
    }

    if (s.acceptanceCriteria && s.acceptanceCriteria.length > 0) {
      md += `## Critères d'Acceptation\n\n`;
      s.acceptanceCriteria.forEach(ac => {
        md += `### ${ac.title}\n`;
        md += `**ID:** ${ac.scenarioId}\n`;
        md += `**Priorité:** ${ac.priority}\n\n`;

        if (ac.gherkin) {
          md += `**Gherkin:**\n`;
          if (ac.gherkin.given) {
            ac.gherkin.given.forEach(g => md += `- **Given** ${g}\n`);
          }
          if (ac.gherkin.when) {
            ac.gherkin.when.forEach(w => md += `- **When** ${w}\n`);
          }
          if (ac.gherkin.then) {
            ac.gherkin.then.forEach(t => md += `- **Then** ${t}\n`);
          }
          md += '\n';
        }
      });
    }

    return md;
  };

  const convertToText = (spec) => {
    const s = spec.specification || spec;
    const title = s.project?.name || s.title || 'Specification';
    let txt = `${title}\n${'='.repeat(title.length)}\n\n`;

    if (s.project?.description || s.summary) {
      txt += `RÉSUMÉ\n${s.summary}\n\n`;
    }

    if (s.functionalDescription) {
      txt += `DESCRIPTION FONCTIONNELLE\n${s.functionalDescription}\n\n`;
    }

    return txt;
  };

  const resetForm = () => {
    setFormData({
      description: '',
      context: '',
      priority: 'medium',
      businessObjectives: '',
      constraints: '',
      stakeholders: [],
      existingFeatures: []
    });
    setGeneratedSpec(null);
    setActiveTab('input');
    setSelectedTemplate('');
  };

  const renderSpecification = () => {
    if (!generatedSpec) return null;

    const { project, feature, stakeholders, metadata } = generatedSpec;

    return (
      <div className="specification-container">
        {/* Project Section */}
        {project && Object.keys(project).length > 0 && (
          <section>
            <h2>{project.name}</h2>
            <p><strong>Goal:</strong> {project.goal}</p>
            {project.constraints?.length > 0 && (
              <ul>
                {project.constraints.map((constraint, index) => (
                  <li key={index}>{constraint}</li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Feature Section */}
        {feature && Object.keys(feature).length > 0 && (
          <section>
            <h2>{feature.name}</h2>
            <p>{feature.description}</p>

            {/* Functional Requirements */}
            {feature.functional_requirements?.length > 0 && (
              <div>
                <h3>Functional Requirements</h3>
                {feature.functional_requirements.map(req => (
                  <div key={req.id}>
                    <h4>{req.id}: {req.description}</h4>
                    {req.acceptance_criteria?.length > 0 && (
                      <ul>
                        {req.acceptance_criteria.map((criteria, index) => (
                          <li key={index}>{criteria}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Non-Functional Requirements */}
            {feature.non_functional_requirements?.length > 0 && (
              <div>
                <h3>Non-Functional Requirements</h3>
                {feature.non_functional_requirements.map(req => (
                  <div key={req.id}>
                    <h4>{req.id}: {req.description}</h4>
                    {req.acceptance_criteria?.length > 0 && (
                      <ul>
                        {req.acceptance_criteria.map((criteria, index) => (
                          <li key={index}>{criteria}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Stakeholders Section */}
        {stakeholders?.length > 0 && (
          <section>
            <h2>Stakeholders</h2>
            <ul>
              {stakeholders.map((stakeholder, index) => (
                <li key={index}><strong>{stakeholder.role}:</strong> {stakeholder.name}</li>
              ))}
            </ul>
          </section>
        )}

        {/* Metadata Section */}
        {metadata && Object.keys(metadata).length > 0 && (
          <section>
            <h2>Metadata</h2>
            <p><strong>Generated At:</strong> {new Date(metadata.generatedAt).toLocaleString()}</p>
            <p><strong>Generated By:</strong> {metadata.generatedBy}</p>
            <p><strong>Input Description:</strong> {metadata.inputDescription}</p>
          </section>
        )}

        {/* Fallback for JSON display */}
        <section>
          <h2>Raw JSON</h2>
          <pre>{JSON.stringify(generatedSpec, null, 2)}</pre>
        </section>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={onClose} className="specification-generator-modal">
      <div className="specification-generator">
        <div className="spec-gen-header">
          <div className="spec-gen-title-section">
            <h2 className="spec-gen-title">
              {t('specificationGenerator.title')}
            </h2>
            <p className="spec-gen-subtitle">
              {t('specificationGenerator.subtitle')}
            </p>
          </div>
          <button onClick={onClose} className="spec-gen-close-btn">
            ✖
          </button>
        </div>

        <div className="spec-gen-tabs">
          <button
            className={`spec-gen-tab ${activeTab === 'input' ? 'active' : ''}`}
            onClick={() => setActiveTab('input')}
          >
            {t('specificationGenerator.tabs.input')}
          </button>
          <button
            className={`spec-gen-tab ${activeTab === 'result' ? 'active' : ''}`}
            onClick={() => setActiveTab('result')}
            disabled={!generatedSpec}
          >
            {t('specificationGenerator.tabs.result')}
          </button>
        </div>

        {activeTab === 'input' && (
          <div className="spec-gen-input-section">
            {/* Affichage des erreurs */}
            {error && (
              <div className="spec-gen-error-alert">
                <div className="spec-gen-error-icon">⚠️</div>
                <div className="spec-gen-error-content">
                  <div className="spec-gen-error-title">Erreur lors de la génération</div>
                  <div className="spec-gen-error-message">{error}</div>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="spec-gen-error-close"
                  aria-label="Fermer l'erreur"
                >
                  ✖
                </button>
              </div>
            )}

            {/* Section Templates */}
            {templates.length > 0 && (
              <div className="spec-gen-section">
                <h3 className="spec-gen-section-title">{t('specificationGenerator.sections.templates')}</h3>
                <div className="template-selector">
                  <select
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateSelect(e.target.value)}
                    className="template-select"
                  >
                    <option value="">{t('specificationGenerator.chooseTemplate')}</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} - {getLocalizedText(template.description, template.description)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Description fonctionnelle */}
            <div className="spec-gen-section">
              <h3 className="spec-gen-section-title">{t('specificationGenerator.functionalDescriptionRequired')}</h3>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('specificationGenerator.fields.description.placeholder')}
                className="spec-gen-textarea large"
                rows={6}
                required
              />
            </div>

            {/* Contexte */}
            <div className="spec-gen-section">
              <h3 className="spec-gen-section-title">{t('specificationGenerator.projectContext')}</h3>
              <textarea
                value={formData.context}
                onChange={(e) => setFormData(prev => ({ ...prev, context: e.target.value }))}
                placeholder={t('specificationGenerator.fields.context.placeholder')}
                className="spec-gen-textarea"
                rows={3}
              />
            </div>

            {/* Priorité et Objectifs Business */}
            <div className="spec-gen-row">
              <div className="spec-gen-section half">
                <h3 className="spec-gen-section-title">{t('specificationGenerator.sections.priority')}</h3>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                  className="spec-gen-select"
                >
                  <option value="low">{t('specificationGenerator.priority.low')}</option>
                  <option value="medium">{t('specificationGenerator.priority.medium')}</option>
                  <option value="high">{t('specificationGenerator.priority.high')}</option>
                  <option value="critical">{t('specificationGenerator.priority.critical')}</option>
                </select>
              </div>

              <div className="spec-gen-section half">
                <h3 className="spec-gen-section-title">{t('specificationGenerator.sections.businessObjectives')}</h3>
                <textarea
                  value={formData.businessObjectives}
                  onChange={(e) => setFormData(prev => ({ ...prev, businessObjectives: e.target.value }))}
                  placeholder={t('specificationGenerator.fields.businessObjectives.placeholder')}
                  className="spec-gen-textarea small"
                  rows={2}
                />
              </div>
            </div>

            {/* Contraintes */}
            <div className="spec-gen-section">
              <h3 className="spec-gen-section-title">{t('specificationGenerator.sections.constraints')}</h3>
              <textarea
                value={formData.constraints}
                onChange={(e) => setFormData(prev => ({ ...prev, constraints: e.target.value }))}
                placeholder={t('specificationGenerator.fields.constraints.placeholder')}
                className="spec-gen-textarea"
                rows={2}
              />
            </div>

            {/* Stakeholders */}
            <div className="spec-gen-section">
              <h3 className="spec-gen-section-title">{t('specificationGenerator.sections.stakeholders')}</h3>

              <div className="stakeholder-input">
                <div className="stakeholder-form">
                  <input
                    type="text"
                    value={newStakeholder.name}
                    onChange={(e) => setNewStakeholder(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t('specificationGenerator.stakeholders.namePlaceholder')}
                    className="stakeholder-field"
                  />
                  <input
                    type="text"
                    value={newStakeholder.role}
                    onChange={(e) => setNewStakeholder(prev => ({ ...prev, role: e.target.value }))}
                    placeholder={t('specificationGenerator.stakeholders.rolePlaceholder')}
                    className="stakeholder-field"
                  />
                  <input
                    type="text"
                    value={newStakeholder.responsibilities}
                    onChange={(e) => setNewStakeholder(prev => ({ ...prev, responsibilities: e.target.value }))}
                    placeholder={t('specificationGenerator.stakeholders.responsibilitiesPlaceholder')}
                    className="stakeholder-field"
                  />
                  <button onClick={addStakeholder} className="add-stakeholder-btn">
                    ➕
                  </button>
                </div>
              </div>

              {formData.stakeholders.length > 0 && (
                <div className="stakeholders-list">
                  {formData.stakeholders.map((stakeholder, index) => (
                    <div key={index} className="stakeholder-item">
                      <div className="stakeholder-info">
                        <strong>{stakeholder.name}</strong> - {stakeholder.role}
                        {stakeholder.responsibilities && (
                          <div className="stakeholder-responsibilities">
                            {stakeholder.responsibilities}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeStakeholder(index)}
                        className="remove-stakeholder-btn"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Options de génération */}
            <div className="spec-gen-section">
              <h3 className="spec-gen-section-title">{t('specificationGenerator.sections.generationOptions')}</h3>

              {/* Section unifiée des options obligatoires */}
              <div className="unified-options">
                {/* Options de base obligatoires */}


                {/* Options avancées obligatoires */}
                <div className="advanced-options">
                  <div className="advanced-mandatory">
                    <div className="advanced-header">
                      <span className="advanced-icon">🚀</span>
                    </div>
                    <div className="advanced-list">
                      <span className="advanced-item contextual">📖 {t('specificationGenerator.options.mandatoryBasic.userStories')}</span>
                      <span className="advanced-item export">✅ {t('specificationGenerator.options.mandatoryBasic.acceptanceCriteria')}</span>
                      <span className="advanced-item export">⚙️ {t('specificationGenerator.options.mandatoryBasic.functionalRequirements')}</span>
                      <span className="advanced-item export">🧪 {t('specificationGenerator.options.mandatoryBasic.testCases')}</span>
                      <span className="advanced-item export">⚠️ {t('specificationGenerator.options.mandatoryBasic.riskAnalysis')}</span>
                      <span className="advanced-item export">💼 {t('specificationGenerator.options.mandatoryBasic.businessValue')}</span>

                      <span className="advanced-item contextual">
                        🧠 {t('specificationGenerator.options.advanced.contextualGeneration.title')}
                        <small>{t('specificationGenerator.options.advanced.contextualGeneration.description')}</small>
                      </span>
                      <span className="advanced-item export">
                        📝 {t('specificationGenerator.options.advanced.jiraExport.title')}
                        <small>{t('specificationGenerator.options.advanced.jiraExport.description')}</small>
                      </span>
                      <span className="advanced-item export">
                        📄 {t('specificationGenerator.options.advanced.confluenceExport.title')}
                        <small>{t('specificationGenerator.options.advanced.confluenceExport.description')}</small>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 🚀 Information permanente sur les bénéfices du mode avancé */}
            {isDescriptionValid() && (
              <div className="spec-gen-section benefits-preview">
                <h3 className="spec-gen-section-title">{t('specificationGenerator.benefitsPreview.title')}</h3>
                <div className="benefits-info">
                  <div className="benefits-grid">
                    <div className="benefit-card contextual">
                      <div className="benefit-icon">🧠</div>
                      <div className="benefit-content">
                        <h4>{t('specificationGenerator.benefitsPreview.contextualGeneration.title')}</h4>
                        <ul>
                          {t('specificationGenerator.benefitsPreview.contextualGeneration.items', { returnObjects: true }).map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="benefit-card export">
                      <div className="benefit-icon">📊</div>
                      <div className="benefit-content">
                        <h4>{t('specificationGenerator.benefitsPreview.automaticExports.title')}</h4>
                        <ul>
                          {t('specificationGenerator.benefitsPreview.automaticExports.items', { returnObjects: true }).map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Boutons d'action */}
            <div className="spec-gen-actions">
              <button
                onClick={generateSpecification}
                disabled={loading || !isDescriptionValid()}
                className="spec-gen-btn primary large"
              >
                {loading ? <Spinner /> : '🎯'} {t('specificationGenerator.actions.generateComplete')}
              </button>

              <button onClick={resetForm} className="spec-gen-btn tertiary" disabled={loading || !isDescriptionValid()}>
                {loading ? <Spinner /> : '🔄'} {t('specificationGenerator.actions.reset')}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'result' && generatedSpec && (
          <div className="spec-gen-result-section">
            <div className="result-header">
              <h3 className="result-title">
                {generatedSpec?.project?.name || generatedSpec?.title || t('specificationGenerator.export.titles.specificationGenerated')}
              </h3>

              <div className="result-actions">
                <div className="export-section">
                  <select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                    className="export-format-select"
                  >
                    <option value="json">JSON</option>
                    <option value="markdown">Markdown</option>
                    <option value="txt">Text</option>
                  </select>
                  <button onClick={exportSpecification} className="export-btn">
                    {t('specificationGenerator.export.buttons.export')}
                  </button>
                </div>

                <button onClick={saveSpecification} disabled={loading} className="save-btn">
                  {loading ? <Spinner /> : '💾'} {t('specificationGenerator.export.buttons.save')}
                </button>

                <CopyButton
                  textToCopy={JSON.stringify(generatedSpec, null, 2)}
                  className="copy-spec-btn"
                />
              </div>
            </div>

            {/* 🧠 Contexte architectural si disponible */}
            {architecturalContext && (
              <div className="architectural-context-panel">
                <h3 className="context-title">{t('specificationGenerator.export.panels.architecturalContext')}</h3>
                <div className="context-stats">
                  <div className="context-stat">
                    <span className="stat-label">{t('specificationGenerator.architecturalContext.nodesAnalyzed')}:</span>
                    <span className="stat-value">{architecturalContext.existingNodes?.length || 0}</span>
                  </div>
                  <div className="context-stat">
                    <span className="stat-label">{t('specificationGenerator.architecturalContext.architecturalStyle')}:</span>
                    <span className="stat-value">{architecturalContext.patterns?.architecturalStyle || architecturalContext.architecturalSummary?.architecturalStyle || t('specificationGenerator.architecturalContext.notAvailable')}</span>
                  </div>
                  <div className="context-stat">
                    <span className="stat-label">{t('specificationGenerator.architecturalContext.dependenciesIdentified')}:</span>
                    <span className="stat-value">{architecturalContext.potentialDependencies?.length || 0}</span>
                  </div>
                  <div className="context-stat">
                    <span className="stat-label">{t('specificationGenerator.architecturalContext.conflictsDetected')}:</span>
                    <span className="stat-value">{architecturalContext.potentialConflicts?.length || 0}</span>
                  </div>
                </div>

                {architecturalContext.potentialDependencies && architecturalContext.potentialDependencies.length > 0 && (
                  <div className="dependencies-section">
                    <h4>🔗 {t('specificationGenerator.architecturalContext.dependenciesTitle')}</h4>
                    <ul className="dependencies-list">
                      {architecturalContext.potentialDependencies.map((dep, index) => (
                        <li key={index} className="dependency-item">
                          <strong>{dep.category}:</strong> {dep.reason}
                          <div className="affected-nodes">
                            {t('specificationGenerator.architecturalContext.nodes')}: {dep.nodes?.map(n => n.name || n).join(', ') || t('specificationGenerator.architecturalContext.notAvailable')}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {architecturalContext.potentialConflicts && architecturalContext.potentialConflicts.length > 0 && (
                  <div className="conflicts-section">
                    <h4>⚠️ Conflits Potentiels</h4>
                    <ul className="conflicts-list">
                      {architecturalContext.potentialConflicts.map((conflict, index) => (
                        <li key={index} className={`conflict-item ${conflict.severity || 'medium'}`}>
                          <strong>{conflict.type}:</strong> {conflict.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* 📊 Exports disponibles */}
            {exportResults && Object.keys(exportResults).length > 0 && (
              <div className="exports-panel">
                <h3 className="exports-title">{t('specificationGenerator.export.panels.exportsGenerated')}</h3>
                <div className="exports-grid">
                  {exportResults.jira && (
                    <div className="export-item jira">
                      <h4>{t('specificationGenerator.export.titles.jiraExport')}</h4>
                      <p>{t('specificationGenerator.export.descriptions.jiraSubtasks', { count: exportResults.jira.subtasks?.length || 0 })}</p>

                      {/* Détails du contenu JIRA */}
                      <div className="export-details">
                        <div className="detail-item">
                          <span className="detail-icon">📋</span>
                          <span>1 Epic: {exportResults.jira.epic?.summary}</span>
                        </div>
                        {exportResults.jira.subtasks && exportResults.jira.subtasks.length > 0 && (
                          <div className="detail-item">
                            <span className="detail-icon">📝</span>
                            <span>{exportResults.jira.subtasks.length} Stories/Tasks:</span>
                            <ul className="subtask-list">
                              {exportResults.jira.subtasks.slice(0, 3).map((task, idx) => (
                                <li key={idx}>• {task.summary}</li>
                              ))}
                              {exportResults.jira.subtasks.length > 3 && (
                                <li>• ... et {exportResults.jira.subtasks.length - 3} autres</li>
                              )}
                            </ul>
                          </div>
                        )}
                        {exportResults.jira.metadata && (
                          <div className="detail-item">
                            <span className="detail-icon">📊</span>
                            <span>Total: {exportResults.jira.metadata.totalItems} éléments</span>
                          </div>
                        )}
                      </div>

                      <div className="export-actions">
                        <button
                          onClick={() => downloadExport('jira', exportResults.jira)}
                          className="export-download-btn"
                        >
                          {t('specificationGenerator.export.buttons.downloadJira')}
                        </button>
                        <CopyButton
                          textToCopy={JSON.stringify(exportResults.jira, null, 2)}
                          className="export-copy-btn"
                        />
                      </div>
                    </div>
                  )}

                  {exportResults.confluence && (
                    <div className="export-item confluence">
                      <h4>{t('specificationGenerator.export.titles.confluenceExport')}</h4>
                      <p>{t('specificationGenerator.export.descriptions.confluenceComplete')}</p>

                      {/* Détails du contenu Confluence */}
                      <div className="export-details">
                        <div className="detail-item">
                          <span className="detail-icon">📄</span>
                          <span>Page: {exportResults.confluence.title}</span>
                        </div>
                        {exportResults.confluence.metadata && exportResults.confluence.metadata.sectionsIncluded && (
                          <div className="detail-item">
                            <span className="detail-icon">📋</span>
                            <span>Sections: {exportResults.confluence.metadata.sectionsIncluded.length}</span>
                            <ul className="section-list">
                              {exportResults.confluence.metadata.sectionsIncluded.slice(0, 4).map((section, idx) => (
                                <li key={idx}>• {section.replace('_', ' ')}</li>
                              ))}
                              {exportResults.confluence.metadata.sectionsIncluded.length > 4 && (
                                <li>• ... et {exportResults.confluence.metadata.sectionsIncluded.length - 4} autres</li>
                              )}
                            </ul>
                          </div>
                        )}
                        <div className="detail-item">
                          <span className="detail-icon">🎨</span>
                          <span>Format HTML Confluence avec tableaux et styles</span>
                        </div>
                      </div>

                      <div className="export-actions">
                        <button
                          onClick={() => downloadExport('confluence', exportResults.confluence)}
                          className="export-download-btn"
                        >
                          {t('specificationGenerator.export.buttons.downloadConfluence')}
                        </button>
                        <CopyButton
                          textToCopy={exportResults.confluence.content}
                          className="export-copy-btn"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="specification-display">
              <ModernSpecificationViewer
                specification={generatedSpec}
                architecturalContext={architecturalContext}
                exportResults={exportResults}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};


export default SpecificationGenerator;
